import { Component, inject, Inject, OnInit } from "@angular/core";
import { NavigationEnd, Router, RouterOutlet } from "@angular/router";
import { ENABLE_PROCESS_AI, UserPermission } from "@ankaadia/ankaadia-shared";
import { AuthService, GenericError } from "@auth0/auth0-angular";
import { TranslocoService } from "@jsverse/transloco";
import { isEmpty } from "lodash";
import { pdfDefaultOptions } from "ngx-extended-pdf-viewer";
import { PrimeNG } from "primeng/config";
import { filter, map, switchMap, take } from "rxjs";
import { environment } from "../../environments/environment";
import { NewUiProcessAgentChatWidgetComponent } from "../features/process-models/process-model-item/process-model-item-editor/process-model-task-editor/sections/advanced/ai-chat/process-agent-chat-widget/new-ui-process-agent-chat-widget.component";
import { TermsAndConditionsService } from "../features/terms-and-conditions/terms-and-conditions.service";
import { NewUiGlobalConfirmationComponent } from "../shared/global-confirmation/new-ui-global-confirmation.component";
import { NewUiGlobalSpinnerComponent } from "../shared/global-spinner/new-ui-global-spinner.component";
import { NewUiGlobalToastComponent } from "../shared/global-toast/new-ui-global-toast.component";
import { getPrimeNgLocaleData } from "../shared/primeng/locale-data/primeng-locale-data";
import { ProgressDialogService } from "../shared/services/progress-dialog.service";
import { ProgressErrorNotifierService } from "../shared/services/progress-error-notifier.service";
import { ProgressNotificationService } from "../shared/services/progress-notification-service";
import { PromptUpdateService } from "../shared/services/promptUpdateService";
import { RouterHistoryService } from "../shared/services/route-stack.service";
import { SettingsService } from "../shared/services/settings.service";

@Component({
  selector: "new-ui-app",
  templateUrl: "./new-ui-app.component.html",
  host: { class: "block h-full w-full overflow-hidden" },
  imports: [
    NewUiGlobalSpinnerComponent,
    NewUiGlobalToastComponent,
    RouterOutlet,
    NewUiGlobalConfirmationComponent,
    NewUiProcessAgentChatWidgetComponent,
  ],
})
export class NewUiAppComponent implements OnInit {
  // Inject these services in order to make sure that they are initialized at the start of the app.
  protected progressNotificationService = inject(ProgressNotificationService);
  protected progressDialog = inject(ProgressDialogService);
  protected progressErrorNotifier = inject(ProgressErrorNotifierService);
  readonly ENABLE_PROCESS_AI = ENABLE_PROCESS_AI;
  protected readonly UserPermission = UserPermission;

  constructor(
    private readonly promptUpdateService: PromptUpdateService,
    @Inject(PrimeNG)
    private readonly primengConfig: PrimeNG,
    private readonly transloco: TranslocoService,
    private readonly authService: AuthService,
    private readonly routerStackService: RouterHistoryService,
    private readonly router: Router,
    protected readonly settings: SettingsService,
    private readonly termsAndConditionsService: TermsAndConditionsService
  ) {}

  ngOnInit(): void {
    // Chats and Consents should only be initialized once and after the initial consents has been showed
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map((event) => event.urlAfterRedirects),
        filter((url) => url.startsWith("/app")),
        switchMap(() => this.settings.isAuthorized$),
        filter((isAuthorized) => isAuthorized),
        take(1)
      )
      .subscribe(() => {
        this.navigateToConsentRouteInCaseConsentsExists();
      });

    this.routerStackService.init();

    this.promptUpdateService.checkForSoftwareUpdate();
    // AuthService must be here to be initialized before the app is loaded. Otherwise login fails
    this.authService.error$.subscribe((error) => {
      // Auth0 reports several distinct failures as the same "Invalid state" message, so log the
      // machine-readable error code plus the login-transaction context needed to tell them apart:
      // a missing `txn` means the sessionStorage transaction was never written in this browsing
      // context (different tab/origin) or was already consumed, and `navType`/`origin`/`referrer`
      // distinguish a re-entered callback URL from a cross-origin hop.
      const [navigationEntry] = performance.getEntriesByType("navigation");
      // eslint-disable-next-line no-console
      console.error("authService.error$", error, {
        code: error instanceof GenericError ? error.error : undefined,
        txn: sessionStorage.getItem(`a0.spajs.txs.${environment.auth0_clientId}`),
        navType: (<PerformanceNavigationTiming>navigationEntry)?.type,
        href: window.location.href,
        origin: window.location.origin,
        referrer: document.referrer,
      });
    });
    this.primengConfig.setTranslation({
      ...this.transloco.translateObject("primeng"),
      ...getPrimeNgLocaleData(this.transloco.getActiveLang()),
    });

    pdfDefaultOptions.assetsFolder = "assets";
  }

  private navigateToConsentRouteInCaseConsentsExists(): void {
    this.termsAndConditionsService
      .getOutStandingConsents({
        consentGiverId: this.settings.userOrCandidateId,
        organizationId: this.settings.organizationId,
      })
      .subscribe((consents) => {
        if (!isEmpty(consents)) {
          void this.router.navigate(["consent", this.settings.organizationId, this.settings.userOrCandidateId], {
            state: { from: this.router.url },
          });
        }
      });
  }
}
